import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bot, Play, Eye, RefreshCw, Settings, Trash2, RotateCcw, Pause, PlayCircle,
  ArrowDownRight, ArrowUpRight, Undo2, ShieldCheck, AlertTriangle, X, ChevronDown, ChevronUp, Plus,
} from 'lucide-react';
import { Trophy, Siren, Swords } from 'lucide-react';
import { dispatch } from '../../dispatch/dispatch';
import { actions } from '../../dispatch';
import WinnersPanel from './WinnersPanel';
import OwnFloorsSection from './OwnFloorsSection';
import ListingIssuesPanel from './ListingIssuesPanel';
import './AutoPilotPanel.css';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface AutoPilotSettings {
  enabled: boolean;
  minProfitAmount: number;
  tierCostThreshold: number;
  tierMinProfit: number;
  competitorFloorEnabled: boolean;
  competitorMinProfit: number;
  competitorMaxCost: number;
  competitorMaxScanAgeDays: number;
  waitDaysNoOrders: number;
  stepPct: number;
  maxStepPct: number;
  minStepMad: number;
  testUpEnabled: boolean;
  testUpMaxAboveStartPct: number;
  testUpPct: number;
  testUpMinOrders: number;
  testUpHoldDays: number;
  maxTestUpFails: number;
  testUpRetryDays: number;
  testUpCooldownDays: number;
  recoverEnabled: boolean;
  patienceFactor: number;
  maxWaitDays: number;
  dropEvalDays: number;
  provenDays: number;
  dropKeepGainPct: number;
  dropCooldownDays: number;
  maxDropFails: number;
  dropRetryDays: number;
  maxChangesPerRun: number;
  minHoursBetweenChanges: number;
  skipOutOfStock: boolean;
  requireFreshPrice: boolean;
  priceMaxAgeHours: number;
  winnerEnabled: boolean;
  winnerApply: boolean;
  winnerMinOrders7d: number;
  winnerTargetPerDay: number;
  winnerPremiumPct: number;
  winnerBigGapPct: number;
  winnerMinSimilarity: number;
  winnerScanPages: number;
  winnerScanMaxAgeHours: number;
}

type Phase = 'idle' | 'dropping' | 'holding' | 'testing_up' | 'at_floor' | 'paused';
type Action = 'drop' | 'test_up' | 'revert' | 'raise' | 'hold' | 'floor' | 'skip' | 'error';

interface HistoryEntry { at: string; type: string; from: number | null; to: number | null; reason: string | null; }

interface OverviewItem {
  _id: string; productId: string; sku: string; storeId: string; storeName: string | null;
  productName: string | null; productImage: string | null; productUrl: string | null; productGroupName: string | null;
  enabled: boolean; phase: Phase; pausedReason: string | null;
  startPrice: number | null; currentPrice: number | null; bestPrice: number | null; floorPrice: number | null;
  profitNow: number | null; stock: number | null; visible: number;
  lastChangeAt: string | null; lastChangeType: string | null; stepsDown: number; stepsUp: number; testUpFails: number;
  lastOrderAt: string | null; ordersSinceChange: number; quietDays: number | null;
  lastEvalAt: string | null; lastDecision: string | null; lastError: string | null; errorCount: number; enrolledAt: string;
  nextAction: { action: Action; to: number | null; reason: string; profitAfter: number | null; preview?: { action: Action; to: number } | null; kind?: string | null } | null;
  competitorFloor?: boolean; competitors?: number | null;
  winner: { _id: string; velocity7d: number; orders7d: number; keyword: string | null; ourRank: number | null; cheapest: number | null; comparables: number; lastScanAt: string | null } | null;
}

interface RunStats { evaluated: number; drops: number; testUps: number; reverts: number; raises?: number; holds: number; atFloor: number; skipped: number; applied: number; failed: number; apiCalls: number; capped: number; }
interface Decision { sku: string; storeName: string | null; productName: string | null; action: Action; from: number | null; to: number | null; profitAfter: number | null; reason: string | null; applied: boolean; deferred?: boolean; error: string | null; }
interface Run { _id: string; trigger: string; dryRun: boolean; startedBy: string | null; startedAt: string; finishedAt: string | null; stats: RunStats; decisions?: Decision[]; error: string | null; }

interface Overview {
  settings: AutoPilotSettings; psychPool: number[]; items: OverviewItem[];
  page: number; limit: number; totalFiltered: number; computedAt: string;
  summary: { total: number; enabled: number; byPhase: Record<string, number>; pendingChanges: number; errors: number; hoursToClearBacklog: number };
  lastRun: Run | null; running: boolean; runningSince: string | null; manualRunning: boolean; defaults: AutoPilotSettings;
}

// Minimal shape of the SKUs the parent page already has (for "Add eligible")
export interface CandidateSku {
  _id: string; sku: string; daysWithoutOrder: number; currentPrice: number | null; missingCost: boolean;
  jumiaQuantity: number | null; visible: number; isProtected: boolean; storeId: string;
}

interface Props {
  token: string;
  storeId: string;
  candidates: CandidateSku[];
  flash: (msg: string) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtMad = (n: number | null | undefined) => (n == null ? '—' : `${Number(n).toFixed(n % 1 === 0 ? 0 : 2)} MAD`);
const fmtDate = (d: string | null | undefined) => (d ? new Date(d).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const ago = (d: string | null | undefined) => {
  if (!d) return '—';
  const ms = Date.now() - new Date(d).getTime();
  const h = Math.floor(ms / 3600000);
  if (h < 1) return `${Math.max(1, Math.floor(ms / 60000))}m ago`;
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const PHASE_META: Record<Phase, { label: string; cls: string; hint: string }> = {
  idle:       { label: 'Idle',       cls: 'ap__phase--idle',    hint: 'Enrolled, no move yet' },
  dropping:   { label: 'Dropping',   cls: 'ap__phase--drop',    hint: 'Lowering price step by step until orders arrive' },
  holding:    { label: 'Sweet spot', cls: 'ap__phase--hold',    hint: 'Getting orders at this price — holding' },
  testing_up: { label: 'Testing ↑',  cls: 'ap__phase--test',    hint: 'Probing a slightly higher price' },
  at_floor:   { label: 'At floor',   cls: 'ap__phase--floor',   hint: 'At minimum profit — cannot go lower' },
  paused:     { label: 'Paused',     cls: 'ap__phase--paused',  hint: 'Repeated errors — needs attention' },
};

const ACTION_META: Record<Action, { label: string; cls: string; Icon: any }> = {
  drop:    { label: 'Drop',    cls: 'ap__act--drop',   Icon: ArrowDownRight },
  test_up: { label: 'Test ↑',  cls: 'ap__act--up',     Icon: ArrowUpRight },
  revert:  { label: 'Revert',  cls: 'ap__act--revert', Icon: Undo2 },
  raise:   { label: 'Fix ↑ floor', cls: 'ap__act--raise', Icon: ShieldCheck },
  hold:    { label: 'Hold',    cls: 'ap__act--hold',   Icon: ShieldCheck },
  floor:   { label: 'Floor',   cls: 'ap__act--floor',  Icon: AlertTriangle },
  skip:    { label: 'Skip',    cls: 'ap__act--skip',   Icon: Pause },
  error:   { label: 'Error',   cls: 'ap__act--error',  Icon: AlertTriangle },
};

const ActionChip: FC<{ action: Action }> = ({ action }) => {
  const m = ACTION_META[action] || ACTION_META.skip;
  return <span className={`ap__act ${m.cls}`}><m.Icon size={11} /> {m.label}</span>;
};

// ─── Settings modal ───────────────────────────────────────────────────────────
const SettingsModal: FC<{
  settings: AutoPilotSettings; defaults: AutoPilotSettings; onClose: () => void; onSave: (s: AutoPilotSettings) => Promise<void>; token: string;
}> = ({ settings, defaults, onClose, onSave, token }) => {
  const [s, setS] = useState<AutoPilotSettings>({ ...settings });
  // Jumia's official fees per category (refreshable from Jumia VendorHub)
  const [fees, setFees] = useState<any>(null);
  const [feesBusy, setFeesBusy] = useState(false);
  useEffect(() => { dispatch(actions.jumiaFeesStatus, {}, token).then((r: any) => !r?.error && setFees(r)); }, [token]);
  const refreshFees = async () => {
    setFeesBusy(true);
    const r: any = await dispatch(actions.jumiaFeesImport, {}, token);
    setFeesBusy(false);
    if (r?.error) alert(r.error.message); else { setFees({ ...fees, ...r }); alert(`Updated from Jumia: ${r.categories} categories, ${r.groups} products re-priced on their real fee.`); }
  };
  // Marjane Auto-Pilot, light — its own on/off switch
  const [mp, setMp] = useState<any>(null);
  useEffect(() => { dispatch(actions.marjanPilotStatus, {}, token).then((r: any) => !r?.error && setMp(r)); }, [token]);
  const toggleMp = async (enabled: boolean) => {
    const r: any = await dispatch(actions.marjanPilotSet, { enabled }, token);
    if (r?.error) alert(r.error.message); else setMp((m: any) => ({ ...(m || {}), enabled: r.enabled }));
  };
  const [saving, setSaving] = useState(false);
  const num = (k: keyof AutoPilotSettings, label: string, hint: string, min = 0, max = 1000, step = 1) => (
    <label className="ap__field" key={k}>
      <span className="ap__field-label">{label}</span>
      <input type="number" min={min} max={max} step={step} value={s[k] as number}
        onChange={e => setS(prev => ({ ...prev, [k]: parseFloat(e.target.value) }))} />
      <span className="ap__field-hint">{hint} <em>(default {String(defaults[k])})</em></span>
    </label>
  );
  const bool = (k: keyof AutoPilotSettings, label: string, hint: string) => (
    <label className="ap__field ap__field--check" key={k}>
      <input type="checkbox" checked={!!s[k]} onChange={e => setS(prev => ({ ...prev, [k]: e.target.checked }))} />
      <span><span className="ap__field-label">{label}</span><span className="ap__field-hint">{hint}</span></span>
    </label>
  );
  return (
    <div className="ap__overlay" onClick={onClose}>
      <div className="ap__modal" onClick={e => e.stopPropagation()}>
        <div className="ap__modal-head">
          <div><Settings size={14} /> Auto-Pilot settings</div>
          <button className="ap__icon-btn" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="ap__modal-body">
          <div className="ap__section-title">💸 Jumia's official fees (commission % + fee per order, by category)</div>
          <div className="ap__fees">
            <span>{fees?.categories ? `${fees.categories.toLocaleString()} categories · imported ${new Date(fees.importedAt).toLocaleDateString()}` : 'not imported yet'}</span>
            <span className="ap__field-hint">Each SKU's floor and profit use the fee of the category it is listed in (Jumia VendorHub → Commissions 2026, dropshipping fee). Refresh when Jumia publishes new fees.</span>
            <button className="ap__btn" disabled={feesBusy} onClick={refreshFees}>{feesBusy ? 'Downloading…' : 'Refresh from Jumia'}</button>
          </div>
          <div className="ap__section-title">🛒 Marjane Auto-Pilot (light)</div>
          <div className="ap__fees">
            <label className="ap__field ap__field--check" style={{ margin: 0 }}>
              <input type="checkbox" checked={!!mp?.enabled} disabled={!mp} onChange={e => toggleMp(e.target.checked)} />
              <span><span className="ap__field-label">{mp?.enabled ? 'ON' : 'OFF'} — runs every 2 hours</span>
                <span className="ap__field-hint">Raises the price of Marjane listings that sell (2 orders) by one step; back to the price that sold if no order in 4 days. No Marjane listing below its product's best seller. Never drops a price on its own; never below 40 MAD profit (or the product's own floor).</span></span>
            </label>
            {mp && <span className="ap__field-hint">Last 7 days: {Object.entries(mp.last7d || {}).map(([k, n]) => `${n} ${k === 'test_up' ? 'test-ups' : k === 'revert' ? 'back to the price that sold' : k === 'lead' ? 'raised to the best seller' : 'raised to the 40 MAD minimum'}`).join(' · ') || 'no change'} · {mp.testing} test{mp.testing === 1 ? '' : 's'} running{mp.lastRun?.at ? ` · last run ${new Date(mp.lastRun.at).toLocaleString()}` : ''}</span>}
          </div>
          <OwnFloorsSection token={token} />
          <div className="ap__section-title">Safety</div>
          <div className="ap__grid">
            {num('minProfitAmount', 'Minimum profit (MAD)', 'Hard floor — never priced below this profit per unit', 0, 10000)}
            {num('tierCostThreshold', 'High-cost threshold (MAD)', 'Products whose BUYING cost is above this use the higher floor below', 0, 10000)}
            {num('tierMinProfit', 'Min profit for high-cost products (MAD)', 'Floor for products with buying cost above the threshold — prices below it are raised automatically', 0, 10000)}
            {num('maxChangesPerRun', 'Max changes per hourly run', 'API safety cap across all stores per run (feeds of 50 SKUs, verified)', 1, 1000)}
            {num('minHoursBetweenChanges', 'Min hours between changes', 'Per SKU — never touched twice within this window', 1, 240)}
            {bool('skipOutOfStock', 'Skip out-of-stock SKUs', 'No orders because no stock is not a price problem')}
            {num('priceMaxAgeHours', 'Trust cached price for (hours)', 'Cached price older than this is re-fetched live before changing (fewer API calls)', 1, 720)}
            {bool('requireFreshPrice', 'Always fetch live price first', 'One extra Jumia call per SKU per change — slower, only if you edit prices by hand often')}
          </div>
          <div className="ap__section-title">⚔ Competitor exception — cheap products facing an outside seller</div>
          <div className="ap__grid">
            {bool('competitorFloorEnabled', 'Allow a lower floor against outside competitors', 'Only when the SKU\'s last competitor scan shows at least one listing that is NOT one of our own stores (own stores are never competitors). Needs a Winners scan for the SKU.')}
            {num('competitorMinProfit', 'Floor with competitor (MAD)', 'Minimum profit allowed for these SKUs instead of the normal minimum', 0, 10000)}
            {num('competitorMaxCost', 'Only if buying cost is below (MAD)', 'The exception never applies to products whose buying cost is at or above this', 0, 10000)}
            {num('competitorMaxScanAgeDays', 'Competitor scan max age (days)', 'Older scans are ignored — the exception switches off until the SKU is scanned again', 1, 60)}
          </div>
          <div className="ap__section-title">Going down (no orders)</div>
          <div className="ap__grid">
            {num('waitDaysNoOrders', 'Min wait days without orders', 'Never drop before this many quiet days. The real patience is scaled per SKU: factor × its usual days between orders', 1, 60)}
            {num('patienceFactor', 'Patience factor', 'Quiet days before a drop = factor × the SKU\'s usual gap between orders (a SKU selling weekly waits ~10 d, not 4)', 0.5, 5, 0.1)}
            {num('maxWaitDays', 'Max wait days', 'Cap of the patience for SKUs that hardly ever sell', 1, 90)}
            {num('stepPct', 'Step %', 'Target drop per step (snapped to a psychological price)', 1, 30, 0.5)}
            {num('maxStepPct', 'Max step %', 'Hard cap for a single move', 1, 40, 0.5)}
            {num('minStepMad', 'Min step (MAD)', 'Never move by less than this', 1, 200)}
          </div>
          <div className="ap__section-title">💰 Profit check — a lower price must earn its keep</div>
          <div className="ap__grid">
            {bool('recoverEnabled', 'Judge every lower price on profit per day', 'Orders/day × profit per unit at each price level. If the lower price does not beat the best proven higher price, the SKU goes back up in one move')}
            {num('dropEvalDays', 'Days before judging (min)', 'Minimum days at the lower price before comparing (also at least the SKU\'s patience)', 1, 60)}
            {num('provenDays', 'Days for a level to count as proven', 'A previous price is only a reference after this many days', 1, 60)}
            {num('dropKeepGainPct', 'Required gain %', 'Keep the lower price only if its profit/day is at least this % above the proven higher price. No sales either way → higher margin wins', 0, 100)}
            {num('dropCooldownDays', 'No new drop for (days)', 'After going back up, the SKU is not dropped again for this long', 1, 365)}
            {num('maxDropFails', 'Drops that did not pay before a rest', 'After this many, the SKU rests at its proven price — a lower price is tried again by itself after the rest', 0, 10)}
            {num('dropRetryDays', 'Rest days after failed drops', 'Counted from the last failed drop. After the rest, a quiet SKU may try a lower price again automatically — no manual reset', 1, 730)}
          </div>
          <div className="ap__section-title">Testing a better price (orders arrived)</div>
          <div className="ap__grid">
            {bool('testUpEnabled', 'Probe higher prices', 'When a price sells, try a slightly higher one — the profit check brings it back if the higher price earns less per day')}
            {num('testUpMaxAboveStartPct', 'Optional probe cap % above start price', '0 = no ceiling (default): a price that keeps paying keeps climbing, step by step. Set a % only if you want to limit how far above the enrolment price probes may go', 0, 500, 5)}
            {num('testUpPct', 'Probe step %', 'Target raise per probe', 1, 30, 0.5)}
            {num('testUpMinOrders', 'Orders before probing', 'Orders required at the current price', 1, 50)}
            {num('testUpHoldDays', 'Stable days before probing', 'Days at the current price before a probe', 1, 60)}
            {num('maxTestUpFails', 'Failed probes before a rest', 'After this many failed probes the SKU rests at its sweet spot — probing restarts by itself', 0, 10)}
            {num('testUpRetryDays', 'Rest days after failed probes', 'After the rest, a SKU that still sells is probed again automatically — no manual reset', 1, 365)}
            {num('testUpCooldownDays', 'Probe cooldown (days)', 'Minimum days between probes', 1, 120)}
          </div>
          <div className="ap__section-title">🏆 Winners — selling SKUs priced daily against the Jumia search page</div>
          <div className="ap__grid">
            {bool('winnerEnabled', 'Enable winners tier', 'Detect selling SKUs every hour and scan their search page nightly (public page, via proxies — no seller-API calls)')}
            {bool('winnerApply', 'Apply winner decisions', 'OFF = preview only (decisions shown in the Winners tab, prices untouched). ON = sent to Jumia, max 1 change / SKU / day')}
            {num('winnerMinOrders7d', 'Orders in 7 days to be a winner', 'Promotion threshold; auto-demoted after 14 quiet days', 1, 50)}
            {num('winnerTargetPerDay', 'Default target orders / day', 'Per-SKU override in the Winners tab. Under target & barely cheapest → push one step down; on target & clearly cheapest → step up', 0.1, 100, 0.5)}
            {num('winnerPremiumPct', 'Premium over unproven listings %', 'A cheaper listing with < 3 reviews or a worse rating is only chased when it is more than this % under us', 0, 30, 0.5)}
            {num('winnerBigGapPct', '"Clearly cheapest" gap %', 'Gap to the next comparable listing above which we are clearly cheapest (capture margin / stop pushing)', 1, 50, 0.5)}
            {num('winnerMinSimilarity', 'Min title similarity (0–1)', 'How alike a listing title must be to count as comparable automatically (you can ✓/✗ by hand)', 0.1, 1, 0.05)}
            {num('winnerScanPages', 'Search pages per keyword', '40 listings per page; 1 is usually enough', 1, 3)}
          </div>
        </div>
        <div className="ap__modal-foot">
          <button className="ap__btn ap__btn--ghost" onClick={() => setS({ ...defaults, enabled: s.enabled })}>Reset to defaults</button>
          <div style={{ flex: 1 }} />
          <button className="ap__btn ap__btn--ghost" onClick={onClose}>Cancel</button>
          <button className="ap__btn ap__btn--primary" disabled={saving} onClick={async () => { setSaving(true); try { await onSave(s); onClose(); } finally { setSaving(false); } }}>
            {saving ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Run detail modal ─────────────────────────────────────────────────────────
const RunModal: FC<{ run: Run; title: string; onClose: () => void }> = ({ run, title, onClose }) => {
  const [filter, setFilter] = useState<'changes' | 'all'>('changes');
  const decisions = (run.decisions || []).filter(d => filter === 'all' || d.to != null || d.error);
  const s = run.stats;
  return (
    <div className="ap__overlay" onClick={onClose}>
      <div className="ap__modal ap__modal--wide" onClick={e => e.stopPropagation()}>
        <div className="ap__modal-head">
          <div><Eye size={14} /> {title}</div>
          <button className="ap__icon-btn" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="ap__modal-body">
          <div className="ap__run-stats">
            <span>Evaluated <b>{s.evaluated}</b></span>
            <span className="ap__act--drop">Drops <b>{s.drops}</b></span>
            <span className="ap__act--up">Test ↑ <b>{s.testUps}</b></span>
            <span className="ap__act--revert">Reverts <b>{s.reverts}</b></span>
            {(s.raises ?? 0) > 0 && <span className="ap__act--raise">Floor fixes ↑ <b>{s.raises}</b></span>}
            <span>Holds <b>{s.holds}</b></span>
            <span>At floor <b>{s.atFloor}</b></span>
            <span>Skipped <b>{s.skipped}</b></span>
            {!run.dryRun && <><span className="ap__ok">Applied <b>{s.applied}</b></span><span className="ap__bad">Failed <b>{s.failed}</b></span><span>API calls <b>{s.apiCalls}</b></span></>}
            {s.capped > 0 && <span className="ap__warn">Waiting for next hour <b>{s.capped}</b></span>}
          </div>
          {run.error && <div className="ap__alert ap__alert--bad">{run.error}</div>}
          <div className="ap__row" style={{ margin: '0.5rem 0' }}>
            <button className={`ap__chip-btn${filter === 'changes' ? ' ap__chip-btn--on' : ''}`} onClick={() => setFilter('changes')}>Price changes only</button>
            <button className={`ap__chip-btn${filter === 'all' ? ' ap__chip-btn--on' : ''}`} onClick={() => setFilter('all')}>All decisions</button>
            <span className="ap__muted" style={{ marginLeft: 'auto' }}>{decisions.length} rows</span>
          </div>
          <div className="ap__table-wrap">
            <table className="ap__table">
              <thead><tr><th>SKU</th><th>Store</th><th>Action</th><th>From</th><th>To</th><th>Profit after</th><th>Reason</th></tr></thead>
              <tbody>
                {decisions.length === 0 && <tr><td colSpan={7} className="ap__muted" style={{ textAlign: 'center', padding: '1rem' }}>Nothing to show</td></tr>}
                {decisions.map((d, i) => (
                  <tr key={i} className={d.error && !d.deferred ? 'ap__tr--error' : d.deferred ? 'ap__tr--off' : ''}>
                    <td><div className="ap__sku">{d.sku}</div><div className="ap__muted ap__ellipsis" title={d.productName || ''}>{d.productName}</div></td>
                    <td>{d.storeName || '—'}</td>
                    <td><ActionChip action={d.action} />{!run.dryRun && d.to != null && (d.applied ? <span className="ap__ok"> ✓</span> : <span className="ap__bad"> ✗</span>)}</td>
                    <td>{fmtMad(d.from)}</td>
                    <td><b>{d.to != null ? fmtMad(d.to) : '—'}</b></td>
                    <td>{d.profitAfter != null ? fmtMad(d.profitAfter) : '—'}</td>
                    <td className="ap__reason">{d.error ? <span className={d.deferred ? 'ap__warn' : 'ap__bad'}>{d.error}</span> : d.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main panel ───────────────────────────────────────────────────────────────
const AutoPilotPanel: FC<Props> = ({ token, storeId, candidates, flash }) => {
  const [view, setView] = useState<'autopilot' | 'winners' | 'issues'>('autopilot');
  const [issueCount, setIssueCount] = useState<number | null>(null);
  const [ov, setOv] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [previewRun, setPreviewRun] = useState<Run | null>(null);
  const [viewRun, setViewRun] = useState<Run | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [showRuns, setShowRuns] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [phaseFilter, setPhaseFilter] = useState<'all' | Phase | 'pending' | 'errors'>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 100;
  const [histories, setHistories] = useState<Record<string, HistoryEntry[] | 'loading'>>({});
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 350); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(0); }, [phaseFilter, debouncedSearch, storeId]);

  const load = useCallback(async (silent = false, refresh = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await dispatch(actions.getAutoPilotOverview, {}, token, { storeId, page, limit: PAGE_SIZE, phase: phaseFilter, q: debouncedSearch, refresh });
      setOv(data); setError(null);
    } catch (e: any) { setError(e?.response?.data?.error?.message || e.message); }
    finally { if (!silent) setLoading(false); }
  }, [token, storeId, page, phaseFilter, debouncedSearch]);

  const loadRuns = useCallback(async () => {
    try { setRuns(await dispatch(actions.getAutoPilotRuns, {}, token, { limit: 20 })); } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  // Listing-issue badge (SKUs with orders whose Jumia listing is not live) — refreshed with the store
  useEffect(() => {
    let alive = true;
    dispatch(actions.getListingIssues, {}, token, { storeId, days: 14 })
      .then((r: any) => { if (alive) setIssueCount((r?.items || []).length); })
      .catch(() => { if (alive) setIssueCount(null); });
    return () => { alive = false; };
  }, [token, storeId]);
  useEffect(() => { loadRuns(); }, [loadRuns]);
  useEffect(() => { setSelected(new Set()); }, [storeId, phaseFilter, debouncedSearch]);

  const loadHistory = useCallback(async (id: string) => {
    setHistories(h => ({ ...h, [id]: 'loading' }));
    try {
      const st = await dispatch(actions.getAutoPilotSku, {}, token, { id });
      setHistories(h => ({ ...h, [id]: st.history || [] }));
    } catch { setHistories(h => ({ ...h, [id]: [] })); }
  }, [token]);

  // Poll while a run is active
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (ov?.running || ov?.manualRunning) {
      pollRef.current = setInterval(async () => {
        try {
          const st = await dispatch(actions.getAutoPilotRunStatus, {}, token);
          if (!st.running) {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            const s: RunStats | undefined = st.last?.stats;
            if (s) flash(`✓ Auto-Pilot run done — ${s.applied} applied · ${s.failed} failed · ${s.holds} holding · ${s.atFloor} at floor`);
            await load(true, true); await loadRuns();
          }
        } catch { /* ignore */ }
      }, 4000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [ov?.running, ov?.manualRunning, token, load, loadRuns, flash]);

  const settings = ov?.settings;

  // ── Actions ─────────────────────────────────────────────────────────────────
  const saveSettings = async (s: AutoPilotSettings) => {
    await dispatch(actions.saveAutoPilotSettings, s, token);
    flash('✓ Auto-Pilot settings saved');
    await load(true, true);
  };

  const toggleMaster = async () => {
    if (!settings) return;
    const next = !settings.enabled;
    if (next && !window.confirm('Enable Auto-Pilot? It will run every hour and change prices of enrolled SKUs on Jumia automatically (within your safety limits: min profit, wait days, max changes per run).')) return;
    await dispatch(actions.saveAutoPilotSettings, { enabled: next }, token);
    flash(next ? '✓ Auto-Pilot ENABLED — runs every hour automatically' : 'Auto-Pilot disabled — no scheduled changes');
    await load(true, true);
  };

  const preview = async () => {
    setBusy('preview');
    try {
      const run: Run = await dispatch(actions.autoPilotPreview, { storeId: storeId !== '0' ? storeId : undefined }, token);
      setPreviewRun(run);
    } catch (e: any) { flash(`⚠ Preview failed: ${e?.response?.data?.error?.message || e.message}`); }
    finally { setBusy(null); }
  };

  const runNow = async () => {
    if (!ov) return;
    const n = ov.summary.pendingChanges;
    if (!window.confirm(`Run Auto-Pilot now?\n\n${n} price change${n === 1 ? '' : 's'} pending (max ${settings?.maxChangesPerRun} per run). Changes are sent to Jumia in verified batches of 50.`)) return;
    setBusy('run');
    try {
      const r = await dispatch(actions.autoPilotRun, { storeId: storeId !== '0' ? storeId : undefined }, token);
      if (r.queued) { flash('⏳ Auto-Pilot run started in background…'); setOv(prev => prev ? { ...prev, manualRunning: true } : prev); }
      else flash(`⚠ ${r.message}`);
    } catch (e: any) { flash(`⚠ Run failed: ${e?.response?.data?.error?.message || e.message}`); }
    finally { setBusy(null); }
  };

  const bulk = async (kind: 'toggle-on' | 'toggle-off' | 'remove' | 'reset' | 'winner') => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (kind === 'winner') {
      setBusy(kind);
      try {
        const r = await dispatch(actions.autoPilotWinnersAdd, { stateIds: ids }, token);
        flash(`🏆 ${r.added} SKU(s) added to Winners — open the Winners view to check keywords`);
        setSelected(new Set());
        await load(true, true);
      } catch (e: any) { flash(`⚠ ${e?.response?.data?.error?.message || e.message}`); }
      finally { setBusy(null); }
      return;
    }
    if (kind === 'remove' && !window.confirm(`Remove ${ids.length} SKU(s) from Auto-Pilot? Prices stay as they are now.`)) return;
    if (kind === 'reset' && !window.confirm(`Reset ${ids.length} SKU(s)? Current price becomes the new start price and history counters restart.`)) return;
    setBusy(kind);
    try {
      if (kind === 'toggle-on')  await dispatch(actions.autoPilotToggle, { stateIds: ids, enabled: true }, token);
      if (kind === 'toggle-off') await dispatch(actions.autoPilotToggle, { stateIds: ids, enabled: false }, token);
      if (kind === 'remove')     await dispatch(actions.autoPilotRemove, { stateIds: ids }, token);
      if (kind === 'reset')      await dispatch(actions.autoPilotReset, { stateIds: ids }, token);
      flash(`✓ ${ids.length} SKU(s) updated`);
      setSelected(new Set());
      await load(true, true);
    } catch (e: any) { flash(`⚠ ${e?.response?.data?.error?.message || e.message}`); }
    finally { setBusy(null); }
  };

  const enrolledIds = useMemo(() => new Set((ov?.items || []).map(i => i.productId)), [ov]);
  const eligible = useMemo(() => {
    const wait = settings?.waitDaysNoOrders ?? 4;
    return candidates.filter(c =>
      !enrolledIds.has(c._id) && !c.missingCost && c.currentPrice && !c.isProtected &&
      c.visible !== 0 && c.jumiaQuantity !== 0 && c.daysWithoutOrder >= wait &&
      (storeId === '0' || c.storeId === storeId)
    );
  }, [candidates, enrolledIds, settings, storeId]);

  const addEligible = async () => {
    if (eligible.length === 0) return;
    if (!window.confirm(`Add ${eligible.length} SKU(s) with no orders for ≥ ${settings?.waitDaysNoOrders} days (with cost, price, stock and live listing) to Auto-Pilot?`)) return;
    setBusy('enroll');
    try {
      const r = await dispatch(actions.autoPilotEnroll, { productIds: eligible.map(e => e._id), enabled: true }, token);
      flash(`✓ Enrolled ${r.created} new · ${r.updated} re-enabled`);
      await load(true, true);
    } catch (e: any) { flash(`⚠ ${e?.response?.data?.error?.message || e.message}`); }
    finally { setBusy(null); }
  };

  const items = ov?.items || [];
  const totalPages = ov ? Math.max(1, Math.ceil(ov.totalFiltered / PAGE_SIZE)) : 1;

  const toggleAll = () => setSelected(selected.size === items.length ? new Set() : new Set(items.map(i => i._id)));
  const toggleOne = (id: string) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const sum = ov?.summary;
  const active = !!(ov?.running || ov?.manualRunning);

  const winnerCount = sum ? (ov?.items || []).filter(i => i.winner).length : 0;
  const viewSwitch = (
    <div className="ap__row" style={{ gap: 6 }}>
      <button className={`ap__chip-btn${view === 'autopilot' ? ' ap__chip-btn--on' : ''}`} onClick={() => setView('autopilot')}><Bot size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Auto-Pilot</button>
      <button className={`ap__chip-btn${view === 'winners' ? ' ap__chip-btn--on' : ''}`} onClick={() => setView('winners')}><Trophy size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Winners{winnerCount ? ` (${winnerCount} on this page)` : ''}</button>
      <button className={`ap__chip-btn${view === 'issues' ? ' ap__chip-btn--on' : ''}${issueCount ? ' ap__chip-btn--alert' : ''}`} onClick={() => setView('issues')} title="SKUs that are getting orders but whose Jumia listing is rejected / deactivated / hidden — check why"><Siren size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Listing issues{issueCount != null ? ` (${issueCount})` : ''}</button>
      {settings && !settings.winnerApply && settings.winnerEnabled && <span className="ap__status ap__status--run" title="Winner decisions are computed and shown but not sent to Jumia until you click Apply in the Winners view">winners in preview</span>}
      {settings?.competitorFloorEnabled && <span className="ap__status ap__status--comp" title={`Cheap products (buying cost < ${settings.competitorMaxCost} MAD) with an outside competitor may go down to ${settings.competitorMinProfit} MAD profit`}><Swords size={11} style={{ verticalAlign: -2 }} /> competitor floor {settings.competitorMinProfit} MAD</span>}
    </div>
  );

  if (view === 'issues') {
    return (
      <div className="ap">
        {viewSwitch}
        <ListingIssuesPanel token={token} storeId={storeId} flash={(m, k) => flash(k === 'bad' ? `⚠ ${m}` : m)} onCount={setIssueCount} />
      </div>
    );
  }

  if (view === 'winners') {
    return (
      <div className="ap">
        {viewSwitch}
        <WinnersPanel token={token} storeId={storeId} settings={settings} flash={(m, k) => flash(k === 'bad' ? `⚠ ${m}` : m)} onSaveSettings={async (patch) => { await dispatch(actions.saveAutoPilotSettings, patch, token); await load(true, true); }} />
      </div>
    );
  }

  return (
    <div className="ap">
      {viewSwitch}
      {/* Header card */}
      <div className="ap__hero">
        <div className="ap__hero-left">
          <div className={`ap__hero-icon${settings?.enabled ? ' ap__hero-icon--on' : ''}`}><Bot size={18} color="#fff" /></div>
          <div>
            <div className="ap__hero-title">Auto-Pilot · sweet-spot pricing
              <span className={`ap__status ${settings?.enabled ? 'ap__status--on' : 'ap__status--off'}`}>{settings?.enabled ? 'ON — runs every hour' : 'OFF — nothing changes automatically'}</span>
              {active && <span className="ap__status ap__status--run"><RefreshCw size={11} className="ap__spin" /> running…</span>}
            </div>
            <div className="ap__hero-sub">
              Quiet for <b>{settings?.patienceFactor}×</b> the SKU's usual gap between orders (min <b>{settings?.waitDaysNoOrders}d</b>, max <b>{settings?.maxWaitDays}d</b>) → drop ~<b>{settings?.stepPct}%</b> to the next psychological price · never below <b>{settings?.minProfitAmount} MAD</b> profit (<b>{settings?.tierMinProfit} MAD</b> when buying cost &gt; {settings?.tierCostThreshold} MAD — too-cheap prices are raised) ·
              {settings?.recoverEnabled && <> <b>💰 profit check:</b> after ≥ <b>{settings.dropEvalDays}d</b> a lower price stays only if it earns ≥ <b>{settings.dropKeepGainPct}%</b> more profit per day than the best proven higher price, otherwise back up in one move (no new drop for {settings.dropCooldownDays}d) · </>}
              orders arrive → hold, then probe <b>+{settings?.testUpPct}%</b> ({settings?.testUpMaxAboveStartPct ? <>up to <b>+{settings.testUpMaxAboveStartPct}%</b> above the start price</> : 'no ceiling'}; a higher price that earns less per day is brought back) · every hour up to <b>{settings?.maxChangesPerRun}</b> changes (verified batches of 50); the rest wait for the next hour.
              {settings?.competitorFloorEnabled && <> · <b>⚔ exception:</b> buying cost &lt; <b>{settings.competitorMaxCost} MAD</b> and an <b>outside</b> competitor on the last scan (our own stores never count) → floor <b>{settings.competitorMinProfit} MAD</b></>}
            </div>
          </div>
        </div>
        <div className="ap__hero-actions">
          <button className="ap__btn ap__btn--ghost" onClick={() => setShowSettings(true)}><Settings size={13} /> Settings</button>
          <button className="ap__btn ap__btn--ghost" onClick={preview} disabled={busy === 'preview' || !ov}>
            {busy === 'preview' ? <RefreshCw size={13} className="ap__spin" /> : <Eye size={13} />} Preview (dry run)
          </button>
          <button className="ap__btn ap__btn--primary" onClick={runNow} disabled={!!busy || active || !ov || (sum?.enabled ?? 0) === 0}>
            <Play size={13} /> Run now
          </button>
          <button className={`ap__btn ${settings?.enabled ? 'ap__btn--danger' : 'ap__btn--success'}`} onClick={toggleMaster} disabled={!settings}>
            {settings?.enabled ? <><Pause size={13} /> Disable scheduler</> : <><PlayCircle size={13} /> Enable scheduler</>}
          </button>
          <button className="ap__icon-btn" onClick={() => load(false, true)} title="Refresh"><RefreshCw size={14} className={loading ? 'ap__spin' : ''} /></button>
        </div>
      </div>

      {error && <div className="ap__alert ap__alert--bad">{error}</div>}

      {/* KPIs */}
      {sum && (
        <div className="ap__kpis">
          <button className={`ap__kpi${phaseFilter === 'all' ? ' ap__kpi--on' : ''}`} onClick={() => setPhaseFilter('all')}><span>Enrolled</span><b>{sum.total}</b><small>{sum.enabled} active</small></button>
          <button className={`ap__kpi${phaseFilter === 'pending' ? ' ap__kpi--on' : ''}`} onClick={() => setPhaseFilter('pending')}><span>Pending changes</span><b className="ap__c-blue">{sum.pendingChanges}</b><small>{sum.pendingChanges > (settings?.maxChangesPerRun ?? 0) ? `≈ ${sum.hoursToClearBacklog} h to clear at ${settings?.maxChangesPerRun}/h` : 'next hourly run'}</small></button>
          <button className={`ap__kpi${phaseFilter === 'dropping' ? ' ap__kpi--on' : ''}`} onClick={() => setPhaseFilter('dropping')}><span>Dropping</span><b className="ap__c-amber">{sum.byPhase.dropping || 0}</b><small>looking for demand</small></button>
          <button className={`ap__kpi${phaseFilter === 'holding' ? ' ap__kpi--on' : ''}`} onClick={() => setPhaseFilter('holding')}><span>Sweet spot</span><b className="ap__c-green">{sum.byPhase.holding || 0}</b><small>selling</small></button>
          <button className={`ap__kpi${phaseFilter === 'testing_up' ? ' ap__kpi--on' : ''}`} onClick={() => setPhaseFilter('testing_up')}><span>Testing ↑</span><b className="ap__c-purple">{sum.byPhase.testing_up || 0}</b><small>probing higher</small></button>
          <button className={`ap__kpi${phaseFilter === 'at_floor' ? ' ap__kpi--on' : ''}`} onClick={() => setPhaseFilter('at_floor')}><span>At floor</span><b className="ap__c-red">{sum.byPhase.at_floor || 0}</b><small>min profit reached</small></button>
          <button className={`ap__kpi${phaseFilter === 'errors' ? ' ap__kpi--on' : ''}`} onClick={() => setPhaseFilter('errors')}><span>Needs attention</span><b className="ap__c-red">{(sum.byPhase.paused || 0) + sum.errors}</b><small>errors / paused</small></button>
        </div>
      )}

      {/* Last run + add eligible */}
      <div className="ap__row ap__row--wrap">
        {ov?.lastRun ? (
          <div className="ap__lastrun">
            Last run {ago(ov.lastRun.startedAt)} ({ov.lastRun.trigger}) — {ov.lastRun.stats.applied} applied · {ov.lastRun.stats.failed} failed · {ov.lastRun.stats.holds} holds · {ov.lastRun.stats.atFloor} at floor
            {ov.lastRun.stats.capped > 0 && <> · <span className="ap__warn">{ov.lastRun.stats.capped} deferred</span></>}
            <button className="ap__link" onClick={async () => { const r = await dispatch(actions.getAutoPilotRun, {}, token, { id: ov.lastRun!._id }); setViewRun(r); }}>details</button>
            <button className="ap__link" onClick={() => { setShowRuns(v => !v); if (!showRuns) loadRuns(); }}>{showRuns ? 'hide history' : 'history'}</button>
          </div>
        ) : <div className="ap__lastrun ap__muted">No run yet — use <b>Preview</b> to see what would happen.</div>}
        <div style={{ flex: 1 }} />
        <button className="ap__btn ap__btn--ghost" onClick={addEligible} disabled={eligible.length === 0 || busy === 'enroll'} title="SKUs from the current table with no orders for the configured wait period, known cost & price, stock > 0, live listing, not protected">
          <Plus size={13} /> Add {eligible.length} eligible no-order SKU{eligible.length === 1 ? '' : 's'}
        </button>
      </div>

      {showRuns && (
        <div className="ap__runs">
          {runs.length === 0 && <div className="ap__muted">No runs yet.</div>}
          {runs.map(r => (
            <div key={r._id} className="ap__run-row">
              <span>{fmtDate(r.startedAt)}</span>
              <span className="ap__muted">{r.trigger}{r.startedBy ? ` · ${r.startedBy}` : ''}</span>
              <span>{r.stats.evaluated} eval</span>
              <span className="ap__act--drop">{r.stats.drops}↓</span>
              <span className="ap__act--up">{r.stats.testUps}↑</span>
              <span className="ap__act--revert">{r.stats.reverts}↺</span>
              {(r.stats.raises ?? 0) > 0 && <span className="ap__act--raise">{r.stats.raises}⤒</span>}
              <span className="ap__ok">{r.stats.applied} ok</span>
              <span className={r.stats.failed ? 'ap__bad' : ''}>{r.stats.failed} fail</span>
              <span className="ap__muted">{r.stats.apiCalls} api</span>
              {r.error && <span className="ap__bad ap__ellipsis" title={r.error}>{r.error}</span>}
              <button className="ap__link" onClick={async () => { const full = await dispatch(actions.getAutoPilotRun, {}, token, { id: r._id }); setViewRun(full); }}>view</button>
            </div>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="ap__row ap__row--wrap ap__toolbar">
        <input className="ap__search" placeholder="Search SKU / product / group…" value={search} onChange={e => setSearch(e.target.value)} />
        <span className="ap__muted">{ov ? `${ov.totalFiltered} SKUs` : ''}{ov && totalPages > 1 ? ` · page ${page + 1}/${totalPages}` : ''}</span>
        {ov && totalPages > 1 && (
          <span className="ap__row">
            <button className="ap__btn ap__btn--sm" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>‹ Prev</button>
            <button className="ap__btn ap__btn--sm" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)}>Next ›</button>
          </span>
        )}
        {selected.size > 0 && (
          <div className="ap__bulk">
            <span>{selected.size} selected</span>
            <button className="ap__btn ap__btn--sm" onClick={() => bulk('toggle-on')} disabled={!!busy}><PlayCircle size={12} /> Enable</button>
            <button className="ap__btn ap__btn--sm" onClick={() => bulk('toggle-off')} disabled={!!busy}><Pause size={12} /> Disable</button>
            <button className="ap__btn ap__btn--sm" onClick={() => bulk('reset')} disabled={!!busy}><RotateCcw size={12} /> Reset</button>
            <button className="ap__btn ap__btn--sm" onClick={() => bulk('winner')} disabled={!!busy} title="Track these SKUs against the Jumia search page daily"><Trophy size={12} /> Make winner</button>
            <button className="ap__btn ap__btn--sm ap__btn--danger" onClick={() => bulk('remove')} disabled={!!busy}><Trash2 size={12} /> Remove</button>
            <button className="ap__link" onClick={() => setSelected(new Set())}>clear</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="ap__table-wrap">
        <table className="ap__table">
          <thead>
            <tr>
              <th style={{ width: 28 }}><input type="checkbox" checked={items.length > 0 && selected.size === items.length} onChange={toggleAll} /></th>
              <th>SKU / product</th>
              <th>Store</th>
              <th>Phase</th>
              <th>Start</th>
              <th>Now</th>
              <th>Floor</th>
              <th>Profit</th>
              <th>Quiet</th>
              <th>Orders*</th>
              <th>Stock</th>
              <th>Next action</th>
              <th>Last change</th>
              <th style={{ width: 24 }} />
            </tr>
          </thead>
          <tbody>
            {loading && !ov && <tr><td colSpan={14} className="ap__muted" style={{ textAlign: 'center', padding: '1.5rem' }}>Loading…</td></tr>}
            {ov && items.length === 0 && (
              <tr><td colSpan={14} className="ap__empty">
                <Bot size={22} />
                <div><b>No SKUs {phaseFilter !== 'all' || debouncedSearch ? 'match this filter' : 'enrolled yet'}.</b></div>
                {phaseFilter === 'all' && !debouncedSearch && <div className="ap__muted">Select SKUs in the <b>All SKUs</b> tab and click <b>Add to Auto-Pilot</b>, or use <b>Add eligible</b> above.</div>}
              </td></tr>
            )}
            {items.map(i => {
              const pm = PHASE_META[i.phase] || PHASE_META.idle;
              const na = i.nextAction;
              const isOpen = expanded === i._id;
              return (
                <Fragment key={i._id}>
                  <tr className={`${!i.enabled ? 'ap__tr--off' : ''}${na?.to != null ? ' ap__tr--pending' : ''}`}>
                    <td><input type="checkbox" checked={selected.has(i._id)} onChange={() => toggleOne(i._id)} /></td>
                    <td>
                      <div className="ap__prod">
                        {i.productImage ? <img src={i.productImage} alt="" className="ap__thumb" /> : <div className="ap__thumb ap__thumb--empty" />}
                        <div>
                          <div className="ap__sku">{i.productUrl ? <a href={i.productUrl} target="_blank" rel="noreferrer">{i.sku}</a> : i.sku}{!i.enabled && <span className="ap__tag">disabled</span>}{i.winner && <span className="ap__tag ap__tag--winner" title={`Winner · ${i.winner.velocity7d}/day · ${i.winner.comparables} comparables${i.winner.ourRank ? ` · rank #${i.winner.ourRank}` : ''}`} onClick={() => setView('winners')} style={{ cursor: 'pointer' }}>🏆 {i.winner.velocity7d}/d</span>}{i.competitorFloor && <span className="ap__tag ap__tag--comp" title={`Competitor exception active: ${i.competitors} outside competitor(s) on the last scan and buying cost below ${settings?.competitorMaxCost} MAD → floor ${settings?.competitorMinProfit} MAD profit`}>⚔ {i.competitors} comp.</span>}</div>
                          <div className="ap__muted ap__ellipsis" title={i.productName || ''}>{i.productName || '—'}</div>
                          {i.productGroupName && <div className="ap__muted ap__ellipsis" style={{ fontSize: '0.68rem' }}>{i.productGroupName}</div>}
                        </div>
                      </div>
                    </td>
                    <td>{i.storeName || '—'}</td>
                    <td><span className={`ap__phase ${pm.cls}`} title={i.pausedReason || pm.hint}>{pm.label}</span>{i.lastError && <div className="ap__bad ap__ellipsis" style={{ maxWidth: 140, fontSize: '0.68rem' }} title={i.lastError}>{i.lastError}</div>}</td>
                    <td>{fmtMad(i.startPrice)}</td>
                    <td><b>{fmtMad(i.currentPrice)}</b>{i.bestPrice != null && i.bestPrice !== i.currentPrice && <div className="ap__muted" style={{ fontSize: '0.68rem' }}>best {i.bestPrice}</div>}</td>
                    <td>{fmtMad(i.floorPrice)}</td>
                    <td className={i.profitNow != null && i.profitNow < (settings?.minProfitAmount ?? 10) ? 'ap__bad' : ''}>{fmtMad(i.profitNow)}</td>
                    <td>{i.quietDays == null ? '—' : i.quietDays >= 9999 ? 'never sold' : `${i.quietDays}d`}</td>
                    <td>{i.ordersSinceChange}</td>
                    <td className={i.stock === 0 ? 'ap__bad' : ''}>{i.stock ?? '—'}</td>
                    <td className="ap__next">
                      {na ? (
                        <>
                          <ActionChip action={na.action} />
                          {na.to != null && <b> → {na.to} MAD</b>}
                          {na.profitAfter != null && <span className="ap__muted"> ({na.profitAfter} profit)</span>}
                          <div className="ap__muted ap__reason-sm" title={na.reason}>{na.reason}</div>
                        </>
                      ) : <span className="ap__muted">disabled</span>}
                    </td>
                    <td>
                      {i.lastChangeAt ? <><div>{ago(i.lastChangeAt)}</div><div className="ap__muted" style={{ fontSize: '0.68rem' }}>{i.lastChangeType} · ↓{i.stepsDown} ↑{i.stepsUp}</div></> : <span className="ap__muted">—</span>}
                    </td>
                    <td><button className="ap__icon-btn" onClick={() => { const open = !isOpen; setExpanded(open ? i._id : null); if (open && !histories[i._id]) loadHistory(i._id); }}>{isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button></td>
                  </tr>
                  {isOpen && (
                    <tr key={`${i._id}-h`} className="ap__tr--detail">
                      <td colSpan={14}>
                        <div className="ap__detail">
                          <div>
                            <div className="ap__section-title">Status</div>
                            <div className="ap__kv"><span>Last evaluated</span><b>{fmtDate(i.lastEvalAt)}</b></div>
                            <div className="ap__kv"><span>Last decision</span><b>{i.lastDecision || '—'}</b></div>
                            <div className="ap__kv"><span>Last order</span><b>{fmtDate(i.lastOrderAt)}</b></div>
                            <div className="ap__kv"><span>Enrolled</span><b>{fmtDate(i.enrolledAt)}</b></div>
                            <div className="ap__kv"><span>Failed probes</span><b>{i.testUpFails}</b></div>
                            {i.pausedReason && <div className="ap__alert ap__alert--bad">{i.pausedReason}</div>}
                          </div>
                          <div>
                            <div className="ap__section-title">History</div>
                            {histories[i._id] === 'loading' && <div className="ap__muted">Loading…</div>}
                            {Array.isArray(histories[i._id]) && (histories[i._id] as HistoryEntry[]).length === 0 && <div className="ap__muted">—</div>}
                            {Array.isArray(histories[i._id]) && (histories[i._id] as HistoryEntry[]).map((h, k) => (
                              <div key={k} className="ap__hist">
                                <span className="ap__muted">{fmtDate(h.at)}</span>
                                <span className={`ap__act ${(ACTION_META as any)[h.type]?.cls || 'ap__act--skip'}`}>{h.type}</span>
                                <span>{h.from != null && h.to != null ? `${h.from} → ${h.to}` : h.from != null ? `${h.from}` : ''}</span>
                                <span className="ap__muted ap__ellipsis" title={h.reason || ''}>{h.reason}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="ap__muted" style={{ fontSize: '0.7rem', marginTop: '0.4rem' }}>* orders (any status except canceled) since the last price change. Prices come from the hourly price sync — use "Sync Live" on a SKU if it looks stale.</div>

      {showSettings && settings && ov && <SettingsModal settings={settings} defaults={ov.defaults} onClose={() => setShowSettings(false)} onSave={saveSettings} token={token} />}
      {previewRun && <RunModal run={previewRun} title={`Preview — what the next run would do (${previewRun.stats.drops + previewRun.stats.testUps + previewRun.stats.reverts} changes)`} onClose={() => setPreviewRun(null)} />}
      {viewRun && <RunModal run={viewRun} title={`Run ${fmtDate(viewRun.startedAt)} (${viewRun.trigger})`} onClose={() => setViewRun(null)} />}
    </div>
  );
};

export default AutoPilotPanel;
